import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Alert, CircularProgress, Card, CardContent, Popper, MenuList, MenuItem, ClickAwayListener } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import ApplicationModal from '../components/ApplicationModal';
import { UserAPI } from '../apis/UserAPI';
import { useAllApplicationsList, clearAllApplicationsCache } from '../components/ApplicationContext';
import { isAuthorized } from '../utils/utilities';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { constants } from '../constants';
import { useThemeDetails } from '../components/ThemeDetailsContext';
const ApplicationManagement = () => {
    const { stateImages } = useThemeDetails();
    const [applications, setApplications] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMenuApp, setSelectedMenuApp] = useState(null);
    // Modal and dialog state
    const [applicationModalOpen, setApplicationModalOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [selectedCard, setSelectedCard] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [applicationToDelete, setApplicationToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [modalError, setModalError] = useState(null);
    // Load data on component mount
    useEffect(() => {
        loadInitialData();
    }, []);
    const getAllApplications = useAllApplicationsList();
    const loadInitialData = async () => {
        setDataLoading(true);
        setError(null);
        try {
            const apps = await getAllApplications();
            // Transform applications to include dateAdded
            const transformedApps = apps.map((app) => ({
                ...app,
                dateAdded: app.metadata?.createdDate
                    ? new Date(app.metadata.createdDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })
                    : constants.application_page_unknown_date
            }));
            setApplications(transformedApps);
        }
        catch (err) {
            console.error('Failed to load applications:', err);
            setError(constants.application_page_load_failed);
            setApplications([]);
        }
        finally {
            setDataLoading(false);
        }
    };
    const handleAddApplication = () => {
        setSelectedApplication(null);
        setModalError(null);
        setApplicationModalOpen(true);
    };
    const handleEditApplication = (app) => {
        setSelectedApplication(app);
        setModalError(null);
        setApplicationModalOpen(true);
    };
    const handleDeleteApplication = (app) => {
        setApplicationToDelete(app);
        setDeleteDialogOpen(true);
    };
    const handleSaveApplication = async (formData, imagePreview) => {
        setLoading(true);
        setModalError(null);
        try {
            if (!formData.name.trim()) {
                setModalError(constants.application_modal_error_name_required);
                setLoading(false);
                return;
            }
            if (!formData.type.trim()) {
                setModalError(constants.application_modal_error_type_required);
                setLoading(false);
                return;
            }
            if (!formData.productName.trim()) {
                setModalError(constants.application_modal_error_product_name_required);
                setLoading(false);
                return;
            }
            if (!formData.url.trim()) {
                setModalError(constants.application_modal_error_url_required);
                setLoading(false);
                return;
            }
            const details = {
                ...selectedApplication?.details,
                productName: formData.productName.trim(),
                productUrl: formData.url.trim(),
                applicationDescription: formData.applicationDescription.trim(),
                applicationState: formData.applicationState
                    ? 'active'
                    : 'inactive',
                // Include image if it's new, otherwise keep existing
                image_base64: imagePreview || selectedApplication?.details?.image_base64
            };
            if (selectedApplication) {
                // Update existing application
                await UserAPI.updateApplication(selectedApplication.id, {
                    organization_id: selectedApplication.organization_id,
                    name: formData.name.trim(),
                    type: formData.type,
                    description: formData.description.trim(),
                    details: details
                });
                // Update local state
                setApplications((prevApps) => prevApps.map((app) => app.id === selectedApplication.id
                    ? {
                        ...app,
                        name: formData.name.trim(),
                        type: formData.type,
                        description: formData.description.trim(),
                        details: details
                    }
                    : app));
                setSuccessMessage(constants.application_page_updated_success);
            }
            else {
                // Create new application
                // Get organization_id from first existing application or use a default
                const organizationId = applications.length > 0
                    ? applications[0].organization_id
                    : 1;
                const response = await UserAPI.createApplication({
                    organization_id: organizationId,
                    name: formData.name.trim(),
                    type: formData.type,
                    description: formData.description.trim(),
                    details: details
                });
                if (!response?.id) {
                    throw new Error(constants.application_page_save_failed);
                }
                // Add new application to local state
                const newApp = {
                    id: response.id,
                    name: formData.name.trim(),
                    type: formData.type,
                    description: formData.description.trim(),
                    organization_id: response.organization_id,
                    resourceType: 'Application',
                    metadata: response.metadata || {
                        versionId: 1,
                        createdDate: new Date().toISOString(),
                        updatedDate: new Date().toISOString(),
                        createdBy: 'current_user',
                        updatedBy: 'current_user'
                    },
                    details: response.details || details,
                    dateAdded: new Date().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })
                };
                setApplications((prevApps) => [...prevApps, newApp]);
                clearAllApplicationsCache();
                setSuccessMessage(constants.application_page_created_success);
            }
            setApplicationModalOpen(false);
            setSelectedApplication(null);
            setModalError(null);
        }
        catch (err) {
            console.error('Error saving application:', err);
            setModalError(err instanceof Error
                ? err.message
                : constants.application_page_save_failed);
        }
        finally {
            setLoading(false);
        }
    };
    const confirmDeleteApplication = async () => {
        if (applicationToDelete) {
            setLoading(true);
            setError(null);
            try {
                await UserAPI.deleteApplication(applicationToDelete.id);
                setApplications((prevApps) => prevApps.filter((a) => a.id !== applicationToDelete.id));
                clearAllApplicationsCache();
                setSuccessMessage(constants.application_page_deleted_success);
                setDeleteDialogOpen(false);
                setApplicationToDelete(null);
            }
            catch (err) {
                console.error('Error deleting application:', err);
                setError(constants.application_page_delete_failed);
            }
            finally {
                setLoading(false);
            }
        }
    };
    // Check view permission
    if (!isAuthorized('RBAC:ViewApplication')) {
        return (_jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.application_page_access_denied }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.application_page_no_permission })] }) }));
    }
    return (_jsxs(Box, { sx: { bgcolor: '#ffffff' }, children: [_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3
                        }, children: [_jsx(Typography, { variant: "body1", sx: { color: '#595959' }, children: constants.application_page_description }), isAuthorized('RBAC:CreateApplication') && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleAddApplication, sx: {
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    backgroundColor: '#26890d',
                                    '&:hover': {
                                        backgroundColor: '#1f6b0a'
                                    }
                                }, children: "Application" }))] }), dataLoading && (_jsxs(Box, { sx: { textAlign: 'center', py: 4 }, children: [stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, {})), _jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: constants.application_page_loading })] })), error && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: error })), successMessage && (_jsx(Alert, { severity: "success", sx: { mb: 3 }, onClose: () => setSuccessMessage(''), children: successMessage })), !dataLoading && !error && (_jsx(_Fragment, { children: applications.length === 0 ? (_jsx(Box, { sx: {
                                textAlign: 'center',
                                py: 6,
                                color: '#595959'
                            }, children: _jsx(Typography, { variant: "body1", children: "No applications found" }) })) : (_jsx(Box, { sx: {
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    md: 'repeat(3, 1fr)',
                                    lg: 'repeat(5, 1fr)'
                                },
                                gap: 3
                            }, children: applications.map((app) => {
                                const isSelected = selectedCard?.id === app.id;
                                return (_jsxs(Card, { onClick: () => setSelectedCard(selectedCard?.id === app.id
                                        ? null
                                        : app), sx: {
                                        width: '100%',
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#FFFFFF',
                                        border: isSelected
                                            ? '2px solid #26890d'
                                            : '1px solid #d9d9d9',
                                        borderRadius: '12px',
                                        boxShadow: isSelected
                                            ? '0 0 0 2px rgba(38, 137, 13, 0.1)'
                                            : 'none',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: isSelected
                                                ? '0 0 0 2px rgba(38, 137, 13, 0.2)'
                                                : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                            border: isSelected
                                                ? '2px solid #26890d'
                                                : '1px solid #bdbdbd'
                                        }
                                    }, children: [isSelected && (_jsx(Box, { sx: {
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                zIndex: 10
                                            }, children: _jsx(Button, { size: "small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    setAnchorEl(e.currentTarget);
                                                    setSelectedMenuApp(app);
                                                }, sx: {
                                                    minWidth: 'auto',
                                                    p: 0.5,
                                                    color: '#212121',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                                                    }
                                                }, children: _jsx(MoreIcon, { sx: {
                                                        fontSize: '24px'
                                                    } }) }) })), isSelected && (_jsx(Box, { sx: {
                                                position: 'absolute',
                                                top: 12,
                                                left: 12,
                                                zIndex: 10,
                                                backgroundColor: '#26890d',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }, children: _jsx(CheckCircleIcon, { sx: {
                                                    fontSize: '24px',
                                                    color: '#ffffff'
                                                } }) })), _jsx(Box, { sx: {
                                                position: 'relative',
                                                width: '100%',
                                                height: {
                                                    xs: '6.5rem',
                                                    sm: '8.5rem'
                                                },
                                                backgroundColor: '#F5F5F5',
                                                borderRadius: '12px 12px 0 0',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: '#F5F5F5',
                                                    opacity: 0.6,
                                                    transition: 'opacity 0.3s ease',
                                                    pointerEvents: 'none',
                                                    borderRadius: '8px 8px 0 0'
                                                }
                                            }, children: _jsx("img", { src: app.details
                                                    ?.image_base64 ||
                                                    'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f0f0f0%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E', alt: `${app.name} logo`, style: {
                                                    height: '100%',
                                                    width: '100%',
                                                    objectFit: 'cover'
                                                } }) }), _jsxs(CardContent, { sx: {
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                p: '16px !important',
                                                gap: 1.5
                                            }, children: [_jsx(Typography, { variant: "h6", color: "#121212", fontWeight: 600, sx: {
                                                        mb: 0.5
                                                    }, children: app.name }), _jsxs(Typography, { variant: "body2", color: "#545454ff", component: "div", sx: {
                                                        minHeight: 40,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        WebkitLineClamp: 6,
                                                        display: '-webkit-box',
                                                        WebkitBoxOrient: 'vertical'
                                                    }, children: [_jsx("span", { style: {
                                                                fontWeight: 800
                                                            }, children: app.description || '' }), _jsx(Box, { component: "span", display: "block", children: app.details
                                                                ?.applicationDescription ||
                                                                '' })] })] })] }, app.id));
                            }) })) }))] }), _jsx(Popper, { open: Boolean(anchorEl), anchorEl: anchorEl, placement: "bottom-end", modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 4]
                        }
                    }
                ], style: { zIndex: 1300 }, children: _jsx(ClickAwayListener, { onClickAway: () => {
                        setAnchorEl(null);
                        setSelectedMenuApp(null);
                    }, children: _jsx(Paper, { sx: {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            minWidth: '160px',
                            overflow: 'hidden'
                        }, children: _jsxs(MenuList, { sx: {
                                py: 0.5
                            }, children: [isAuthorized('RBAC:UpdateApplication') && (_jsxs(MenuItem, { onClick: () => {
                                        if (selectedMenuApp) {
                                            handleEditApplication(selectedMenuApp);
                                        }
                                        setAnchorEl(null);
                                        setSelectedMenuApp(null);
                                    }, sx: {
                                        py: 1,
                                        px: 2,
                                        fontSize: '14px',
                                        color: '#212121',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }, children: [_jsx(EditIcon, { sx: {
                                                mr: 1.5,
                                                fontSize: '18px',
                                                color: '#595959'
                                            } }), constants.application_page_edit] })), isAuthorized('RBAC:DeleteApplication') && (_jsxs(MenuItem, { onClick: () => {
                                        if (selectedMenuApp) {
                                            handleDeleteApplication(selectedMenuApp);
                                        }
                                        setAnchorEl(null);
                                        setSelectedMenuApp(null);
                                    }, sx: {
                                        py: 1,
                                        px: 2,
                                        fontSize: '14px',
                                        color: '#212121',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }, children: [_jsx(DeleteIcon, { sx: {
                                                mr: 1.5,
                                                fontSize: '18px',
                                                color: '#595959'
                                            } }), constants.application_page_delete] }))] }) }) }) }), _jsx(ApplicationModal, { open: applicationModalOpen, onClose: () => {
                    setApplicationModalOpen(false);
                    setSelectedApplication(null);
                    setModalError(null);
                }, onSave: handleSaveApplication, application: selectedApplication, loading: loading, error: modalError }), _jsx(ConfirmationDialog, { isOpen: deleteDialogOpen, setIsOpen: () => {
                    setDeleteDialogOpen(false);
                    setApplicationToDelete(null);
                }, submitFunction: confirmDeleteApplication, bodyText: `Are you sure you want to delete "${applicationToDelete?.name}"? This action cannot be undone.`, submitButtonText: "Delete", loading: loading })] }));
};
export default authenticatedRoute(ApplicationManagement);
